/**
 * RuleValueCombobox — the scan-rule value picker (TRA-1062).
 *
 * A searchable dropdown over the values already observed on the team's
 * traffic, with free entry: typing a value that is not in the list offers
 * it as a new entry. Suggestions come from the field-values endpoint and
 * differ per metadata field.
 */

import { useState } from 'react'
import { Check, ChevronsUpDown } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'

export function RuleValueCombobox({
  value,
  suggestions,
  onChange,
  ariaLabel,
}: {
  value: string
  suggestions: readonly string[]
  onChange: (value: string) => void
  ariaLabel: string
}): React.JSX.Element {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')

  const trimmed = search.trim()
  const showCreate = trimmed !== '' && !suggestions.includes(trimmed)

  const pick = (picked: string): void => {
    onChange(picked)
    setSearch('')
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel}
          className={cn(
            'w-full justify-between font-normal',
            value === '' && 'text-muted-foreground'
          )}
        >
          <span className="truncate">{value === '' ? 'value' : value}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command>
          <CommandInput
            placeholder="Search or type a new value"
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>No observed values match.</CommandEmpty>
            <CommandGroup>
              {showCreate && (
                <CommandItem
                  value={trimmed}
                  onSelect={() => {
                    pick(trimmed)
                  }}
                >
                  Use &ldquo;{trimmed}&rdquo;
                </CommandItem>
              )}
              {suggestions.map(suggestion => (
                <CommandItem
                  key={suggestion}
                  value={suggestion}
                  onSelect={() => {
                    pick(suggestion)
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === suggestion ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <span className="truncate">{suggestion}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
